import type { BookRawJson } from './bookSchema';

export type AdminMe = {
  uuid: string;
  email: string;
  role: string;
  totp_enabled: boolean;
  is_active: boolean;
  created_at: string;
};

export type AdminPublic = {
  uuid: string;
  email: string;
  role: string;
  totp_enabled: boolean;
  created_at: string;
};

/**
 * Step-1 login result. When `requires_totp` is true the caller MUST
 * follow up with `loginWithTOTP(totp_token, code)` to receive a real
 * access token; the `access_token` field is empty in that case.
 */
export type LoginResponse = {
  requires_totp: boolean;
  token_type?: 'Bearer';
  access_token?: string;
  access_expires_at?: string;
  totp_token?: string;
  totp_expires_at?: string;
  admin?: AdminPublic;
};

export type TOTPSetupResponse = {
  secret: string;
  otpauth_url: string;
  account_label: string;
  issuer: string;
};

export type BookStatus = 'draft' | 'published' | 'archived';

export type BookListItem = {
  uuid: string;
  title: string;
  authors: string[];
  status: BookStatus;
  is_free: boolean;
  created_at: string;
};

export type ListBooksResponse = {
  items: BookListItem[];
  limit: number;
  offset: number;
};

export type BookRowResponse = {
  uuid: string;
  status: BookStatus;
  is_free: boolean;
  audio_cover_filename: string;
  book_cover_filename: string;
  audio_demo_filename: string;
  audio_full_filename: string;
  subtitle_full_vtt_filename: string | null;
  subtitle_full_json_filename: string | null;
  subtitle_demo_vtt_filename: string | null;
  subtitle_demo_json_filename: string | null;
  audio_full_minutes: number;
  pdf_filename: string | null;
  audio_text: string;
  metadata: any;
  legal: any;
  faq: any;
  action_plan: any;
  mind_map: any;
  glossary: any;
  simulation: any;
  social_snaps: any;
  key_takeaways: any;
  who_is_this_for: any;
  unplay_assessment: any;
  upload_id: string | null;
  created_at: string;
};

export type CreateBookResponse = {
  uuid: string;
  created_at: string;
};

export type UpsertBookPayload = {
  status: BookStatus;
  is_free: boolean;
  audio_cover_filename: string;
  book_cover_filename: string;
  audio_demo_filename: string;
  audio_full_filename: string;
  subtitle_full_vtt_filename?: string;
  subtitle_full_json_filename?: string;
  subtitle_demo_vtt_filename?: string;
  subtitle_demo_json_filename?: string;
  audio_full_minutes: number;
  pdf_filename?: string;
  audio_text: string;
  content_json: BookRawJson;
};

export type PresignUploadRequest = {
  kind: 'audio_demo' | 'audio_full' | 'image' | 'pdf' | 'socialsnap' | 'subtitle';
  file_name: string;
  content_type: string;
  size_bytes: number;
};

export type PresignUploadResponse = {
  key: string;
  upload_url: string;
  headers: Record<string, string>;
  public_url?: string;
  expires_at: string;
};

export type StatsResponse = {
  server_time: string;
  counts: {
    books: number;
    users: number;
    admins: number;
  };
  recent: {
    books: Array<{
      uuid: string;
      title: string;
      authors: string[];
      created_at: string;
    }>;
    users: UserListItem[];
  };
};

export type UserListItem = {
  uuid: string;
  email: string;
  name: string;
  picture_url: string;
  google_id: string;
  has_password: boolean;
  email_verified: boolean;
  locale: string;
  created_at: string;
  last_login_at?: string;
  last_active_at?: string;
  deleted_at?: string;
  book_count: number;
  finished_count: number;
};

export type ListUsersResponse = {
  items: UserListItem[];
  total: number;
  limit: number;
  offset: number;
  q: string;
  status: 'active' | 'deleted' | 'all';
  sort: string;
  order: 'asc' | 'desc';
};

export type UserLibraryEntry = {
  book_uuid: string;
  title: string;
  status: string;
  progress_seconds: number;
  progress_pct: number;
  updated_at: string;
  finished_at?: string;
};

export type UserDetail = UserListItem & {
  deletion_reason: string;
  updated_at?: string;
  library_stats: {
    saved: number;
    listening: number;
    finished: number;
    archived: number;
    total_seconds_listened: number;
  };
  recent_library: UserLibraryEntry[];
};

export type ListUsersParams = {
  q?: string;
  status?: 'active' | 'deleted' | 'all';
  sort?: 'created_at' | 'last_active_at' | 'last_login_at';
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
};

// ─── Comment moderation ───────────────────────────────────────────

export type CommentStatus = 'pending' | 'approved' | 'rejected';

/** Why a reader flagged a comment. Closed set, enforced by the DB. */
export type CommentReportReason =
  | 'spam'
  | 'harassment'
  | 'hate'
  | 'sexual'
  | 'violence'
  | 'misinformation'
  | 'other';

/** Filters the queue accepts. `reported` cuts across the three stored
 *  statuses: it selects whatever readers have flagged, regardless of
 *  whether it is currently published. */
export type CommentStatusFilter = CommentStatus | 'all' | 'reported';

export type CommentListItem = {
  id: string;
  text: string;
  status: CommentStatus;
  created_at: string;
  moderated_at?: string;
  /** Email of the admin who last acted on it; empty while pending. */
  moderated_by: string;
  moderation_note: string;
  likes_count: number;
  /** Reader reports nobody has ruled on yet. */
  reports_open: number;
  /** Distinct reasons behind those open reports. */
  report_reasons: CommentReportReason[];
  book_uuid: string;
  book_title: string;
  book_slug: string;
  user_uuid: string;
  user_name: string;
  user_email: string;
  user_picture_url: string;
};

export type CommentCounts = {
  pending: number;
  approved: number;
  rejected: number;
  /** Comments with unresolved reports — overlaps the three above. */
  reported: number;
  total: number;
};

export type ListCommentsResponse = {
  items: CommentListItem[];
  total: number;
  /** Per-status totals for the current search scope, for the tab badges. */
  counts: CommentCounts;
  limit: number;
  offset: number;
  q: string;
  status: CommentStatusFilter;
  sort: string;
  order: 'asc' | 'desc';
};

export type ListCommentsParams = {
  status?: CommentStatusFilter;
  q?: string;
  book_uuid?: string;
  sort?: 'created_at' | 'moderated_at';
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
};

export type BulkCommentAction = 'approve' | 'reject' | 'pending' | 'delete';

export type ReportsOverview = {
  server_time: string;
  totals: {
    books: number;
    users: number;
    users_deleted: number;
    admins: number;
    library_items: number;
  };
  active: { dau: number; wau: number; mau: number };
  listening: {
    total_seconds: number;
    avg_minutes_per_user: number;
    books_with_listeners: number;
  };
  engagement: {
    signups_last_7d: number;
    signups_last_30d: number;
    finished_last_30d: number;
  };
  new_users_30d: Array<{ date: string; value: number }>;
  recent_audit: AuditItem[];
};

export type TopBook = {
  uuid: string;
  title: string;
  authors: string[];
  score: number;
};

export type AuditItem = {
  uuid: string;
  admin_email: string;
  action: string;
  target_kind: string;
  target_uuid?: string;
  reason: string;
  ip: string;
  created_at: string;
};

export type AdminListItem = {
  uuid: string;
  email: string;
  role: string;
  created_at: string;
};

export type ListAdminsResponse = {
  items: AdminListItem[];
  limit: number;
  offset: number;
  q: string;
};

// ─── Taxonomy (authors / categories / publishers / tags) ──────────

export type TaxonomyKind = 'authors' | 'categories' | 'publishers' | 'tags';

export type TaxonomyItem = {
  uuid: string;
  slug: string | null;
  name: string;
  image_filename: string;
  description: string;
  website_url: string;
  book_count: number;
  created_at: string;
};

/**
 * Payload for creating or updating a taxonomy entry. `image_filename`
 * is the bare R2 key (e.g. "<uuid>.jpg") returned by the presign +
 * PUT upload flow. `website_url` is only honoured for publishers.
 */
export type TaxonomyUpsert = {
  name: string;
  image_filename?: string;
  description?: string;
  website_url?: string;
};

export type ListTaxonomyResponse = {
  kind: TaxonomyKind;
  items: TaxonomyItem[];
  total: number;
  limit: number;
  offset: number;
  q: string;
};

// ─── Bulk import ──────────────────────────────────────────────────

export type ImportBookEntry = {
  status?: BookStatus;
  is_free: boolean;
  audio_cover_filename: string;
  book_cover_filename: string;
  audio_demo_filename: string;
  audio_full_filename: string;
  audio_full_minutes: number;
  pdf_filename?: string;
  audio_text: string;
  content_json: any;
  mode?: 'create' | 'upsert';
};

export type ImportResult = {
  index: number;
  status: 'created' | 'updated' | 'skipped' | 'error';
  uuid?: string;
  title?: string;
  error?: string;
};

export type ImportResponse = {
  dry_run: boolean;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errored: number;
  results: ImportResult[];
  duration_ms: number;
};

// ─── Advanced reports ─────────────────────────────────────────────

export type RetentionCohort = {
  cohort_week: string;
  size: number;
  retention: number[];
};

export type RetentionResponse = {
  weeks: number;
  cohorts: RetentionCohort[];
};

export type FunnelStep = { key: string; label: string; count: number };
export type FunnelResponse = { days: number; steps: FunnelStep[] };

export type BookEngagementBucket = {
  bucket_start: number;
  bucket_end: number;
  count: number;
};
export type BookEngagement = {
  book_uuid: string;
  listeners: number;
  finished: number;
  avg_progress_pct: number;
  dropoff_buckets: BookEngagementBucket[];
};

export type CategoryListItem = {
  uuid: string;
  name: string;
  book_count: number;
  created_at: string;
};

export type ListCategoriesResponse = {
  items: CategoryListItem[];
  total: number;
  limit: number;
  offset: number;
};

type ApiError = {
  code: string;
  message: string;
};

const DEFAULT_BASE_URL = 'http://localhost:5001';

function baseUrl(): string {
  const v = (import.meta as any).env?.VITE_PANEL_API_BASE_URL;
  return typeof v === 'string' && v.trim() ? v.trim() : DEFAULT_BASE_URL;
}

async function parseJsonOrText(res: Response): Promise<any> {
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return await res.json();
  return await res.text();
}

async function apiFetch(path: string, init?: RequestInit): Promise<any> {
  const res = await fetch(`${baseUrl()}${path}`, init);
  if (res.ok) {
    if (res.status === 204) return null;
    return await parseJsonOrText(res);
  }

  const body = await parseJsonOrText(res);
  const msg =
    body && typeof body === 'object' && 'message' in body
      ? String((body as ApiError).message)
      : `Request failed with status ${res.status}`;
  const code = body && typeof body === 'object' && 'code' in body ? String((body as ApiError).code) : 'request_failed';
  const err = new Error(`${code}: ${msg}`);
  (err as any).status = res.status;
  (err as any).code = code;
  (err as any).details = body;
  throw err;
}

function authHeaders(accessToken: string): HeadersInit {
  return {
    Authorization: `Bearer ${accessToken}`,
  };
}

function normalizeBookStatus(value: unknown): BookStatus {
  const raw = String(value ?? '').trim().toLowerCase();
  if (raw === 'published' || raw === 'publish' || raw === 'public' || raw === 'live' || raw === 'active') {
    return 'published';
  }
  if (raw === 'archived' || raw === 'archive' || raw === 'retired') {
    return 'archived';
  }
  return 'draft';
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  return (await apiFetch('/admin/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })) as LoginResponse;
}

export async function loginWithTOTP(totpToken: string, code: string): Promise<LoginResponse> {
  return (await apiFetch('/admin/auth/login/totp', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ totp_token: totpToken, code }),
  })) as LoginResponse;
}

export async function totpSetup(accessToken: string): Promise<TOTPSetupResponse> {
  return (await apiFetch('/admin/auth/2fa/setup', {
    method: 'POST',
    headers: { ...authHeaders(accessToken) },
  })) as TOTPSetupResponse;
}

export async function totpEnable(accessToken: string, code: string): Promise<{ status: 'enabled' }> {
  return (await apiFetch('/admin/auth/2fa/enable', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify({ code }),
  })) as { status: 'enabled' };
}

export async function totpDisable(
  accessToken: string,
  payload: { password: string; code: string },
): Promise<{ status: 'disabled' }> {
  return (await apiFetch('/admin/auth/2fa/disable', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify(payload),
  })) as { status: 'disabled' };
}

export async function me(accessToken: string): Promise<AdminMe> {
  return (await apiFetch('/admin/me', {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as AdminMe;
}

export async function stats(accessToken: string): Promise<StatsResponse> {
  return (await apiFetch('/admin/stats', {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as StatsResponse;
}

export async function listBooks(accessToken: string, limit = 50, offset = 0): Promise<ListBooksResponse> {
  const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) }).toString();
  const res = (await apiFetch(`/admin/books?${qs}`, {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as ListBooksResponse;
  return {
    ...res,
    items: (res.items ?? []).map((item) => ({ ...item, status: normalizeBookStatus(item.status) })),
  };
}

export async function getBook(accessToken: string, uuid: string): Promise<BookRowResponse> {
  const res = (await apiFetch(`/admin/books/${encodeURIComponent(uuid)}`, {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as BookRowResponse;
  return {
    ...res,
    status: normalizeBookStatus(res.status),
  };
}

export type BookSnap = {
  uuid: string;
  filename: string;
  snap_index: number;
  url: string;
};

export async function listBookSnaps(accessToken: string, uuid: string): Promise<{ items: BookSnap[] }> {
  try {
    return (await apiFetch(`/admin/books/${encodeURIComponent(uuid)}/snaps`, {
      method: 'GET',
      headers: { ...authHeaders(accessToken) },
    })) as { items: BookSnap[] };
  } catch (err) {
    if ((err as any)?.status === 404) {
      return { items: [] };
    }
    throw err;
  }
}

export async function createBook(accessToken: string, payload: UpsertBookPayload): Promise<CreateBookResponse> {
  return (await apiFetch('/admin/books', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify(payload),
  })) as CreateBookResponse;
}

export async function updateBook(accessToken: string, uuid: string, payload: UpsertBookPayload): Promise<CreateBookResponse> {
  return (await apiFetch(`/admin/books/${encodeURIComponent(uuid)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify(payload),
  })) as CreateBookResponse;
}

export async function deleteBook(accessToken: string, uuid: string): Promise<void> {
  await apiFetch(`/admin/books/${encodeURIComponent(uuid)}`, {
    method: 'DELETE',
    headers: { ...authHeaders(accessToken) },
  });
}

export async function presignUpload(accessToken: string, payload: PresignUploadRequest): Promise<PresignUploadResponse> {
  return (await apiFetch('/admin/uploads/presign', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify(payload),
  })) as PresignUploadResponse;
}

/** Map an image File to a Content-Type the backend accepts. */
function imageContentType(file: File): string {
  const t = (file.type || '').toLowerCase();
  if (t.startsWith('image/')) return t;
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  if (ext === 'png') return 'image/png';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'svg') return 'image/svg+xml';
  return 'application/octet-stream';
}

/**
 * Upload an image straight to the Cloudflare R2 images bucket.
 *
 * The backend presign step renames the object to a UUID key
 * (`<uuid>.<ext>`), so the caller never has to invent a filename — it
 * just persists the returned `key` on the owning entity. `onProgress`
 * reports 0–100 during the PUT.
 */
export async function uploadImageToR2(
  accessToken: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<{ key: string; public_url?: string }> {
  const presigned = await presignUpload(accessToken, {
    kind: 'image',
    file_name: file.name,
    content_type: imageContentType(file),
    size_bytes: file.size,
  });

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presigned.upload_url, true);
    for (const [k, v] of Object.entries(presigned.headers)) xhr.setRequestHeader(k, v);
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
    });
    xhr.addEventListener('error', () => reject(new Error('Upload failed: network error')));
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));
    xhr.send(file);
  });

  return { key: presigned.key, public_url: presigned.public_url };
}

export async function listUsers(
  accessToken: string,
  params: ListUsersParams = {},
): Promise<ListUsersResponse> {
  const qs = new URLSearchParams();
  if (params.q != null) qs.set('q', params.q);
  if (params.status) qs.set('status', params.status);
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  qs.set('limit', String(params.limit ?? 50));
  qs.set('offset', String(params.offset ?? 0));
  return (await apiFetch(`/admin/users?${qs.toString()}`, {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as ListUsersResponse;
}

export async function getUser(accessToken: string, uuid: string): Promise<UserDetail> {
  return (await apiFetch(`/admin/users/${encodeURIComponent(uuid)}`, {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as UserDetail;
}

export async function deleteUserAccount(
  accessToken: string,
  uuid: string,
  reason: string,
): Promise<void> {
  await apiFetch(`/admin/users/${encodeURIComponent(uuid)}/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify({ reason }),
  });
}

// ─── Comment moderation ───────────────────────────────────────────

export async function listComments(
  accessToken: string,
  params: ListCommentsParams = {},
): Promise<ListCommentsResponse> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.q != null) qs.set('q', params.q);
  if (params.book_uuid) qs.set('book_uuid', params.book_uuid);
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  qs.set('limit', String(params.limit ?? 25));
  qs.set('offset', String(params.offset ?? 0));
  return (await apiFetch(`/admin/comments?${qs.toString()}`, {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as ListCommentsResponse;
}

/** Backlog size for the sidebar badge — cheaper than a full list call. */
export async function commentCounts(accessToken: string): Promise<CommentCounts> {
  return (await apiFetch('/admin/comments/counts', {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as CommentCounts;
}

export async function moderateComment(
  accessToken: string,
  id: string,
  status: CommentStatus,
  note = '',
): Promise<void> {
  await apiFetch(`/admin/comments/${encodeURIComponent(id)}/moderate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify({ status, note }),
  });
}

/** Redact part of an otherwise publishable comment. */
export async function updateCommentText(
  accessToken: string,
  id: string,
  text: string,
): Promise<void> {
  await apiFetch(`/admin/comments/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify({ text }),
  });
}

export async function deleteComment(accessToken: string, id: string): Promise<void> {
  await apiFetch(`/admin/comments/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { ...authHeaders(accessToken) },
  });
}

/**
 * Clears the reader reports against a comment without changing the
 * comment — for a flag that turned out to be a disagreement rather than
 * a violation. Approving or rejecting resolves reports on its own, so
 * this is only for leaving a published comment exactly as it is.
 */
export async function dismissCommentReports(
  accessToken: string,
  id: string,
  note = '',
): Promise<{ id: string; dismissed: number }> {
  return (await apiFetch(`/admin/comments/${encodeURIComponent(id)}/reports/dismiss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify({ note }),
  })) as { id: string; dismissed: number };
}

export async function bulkModerateComments(
  accessToken: string,
  ids: string[],
  action: BulkCommentAction,
  note = '',
): Promise<{ action: string; affected: number }> {
  return (await apiFetch('/admin/comments/bulk', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify({ ids, action, note }),
  })) as { action: string; affected: number };
}

export async function reportsOverview(accessToken: string): Promise<ReportsOverview> {
  return (await apiFetch('/admin/reports/overview', {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as ReportsOverview;
}

export async function reportsTopBooks(
  accessToken: string,
  metric: 'listened' | 'saved' | 'finished' = 'listened',
  limit = 10,
): Promise<{ metric: string; items: TopBook[] }> {
  const qs = new URLSearchParams({ metric, limit: String(limit) }).toString();
  return (await apiFetch(`/admin/reports/top-books?${qs}`, {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as { metric: string; items: TopBook[] };
}

// ─── Taxonomy ─────────────────────────────────────────────────────

export async function listTaxonomy(
  accessToken: string,
  kind: TaxonomyKind,
  params: { q?: string; sort?: 'name' | 'book_count' | 'created_at'; limit?: number; offset?: number } = {},
): Promise<ListTaxonomyResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.sort) qs.set('sort', params.sort);
  qs.set('limit', String(params.limit ?? 100));
  qs.set('offset', String(params.offset ?? 0));
  return (await apiFetch(`/admin/taxonomy/${kind}?${qs.toString()}`, {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as ListTaxonomyResponse;
}

export async function createTaxonomy(
  accessToken: string,
  kind: TaxonomyKind,
  payload: TaxonomyUpsert,
): Promise<TaxonomyItem> {
  return (await apiFetch(`/admin/taxonomy/${kind}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify(payload),
  })) as TaxonomyItem;
}

export async function updateTaxonomy(
  accessToken: string,
  kind: TaxonomyKind,
  uuid: string,
  payload: TaxonomyUpsert,
): Promise<void> {
  await apiFetch(`/admin/taxonomy/${kind}/${encodeURIComponent(uuid)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify(payload),
  });
}

export async function deleteTaxonomy(
  accessToken: string,
  kind: TaxonomyKind,
  uuid: string,
  force = false,
): Promise<void> {
  const qs = force ? '?force=true' : '';
  await apiFetch(`/admin/taxonomy/${kind}/${encodeURIComponent(uuid)}${qs}`, {
    method: 'DELETE',
    headers: { ...authHeaders(accessToken) },
  });
}

export async function mergeTaxonomy(
  accessToken: string,
  kind: TaxonomyKind,
  srcUUID: string,
  intoUUID: string,
): Promise<void> {
  await apiFetch(`/admin/taxonomy/${kind}/${encodeURIComponent(srcUUID)}/merge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify({ into_uuid: intoUUID }),
  });
}

// ─── Bulk import ──────────────────────────────────────────────────

export async function importBooks(
  accessToken: string,
  items: ImportBookEntry[],
  dryRun: boolean,
): Promise<ImportResponse> {
  return (await apiFetch(`/admin/books/import?dry_run=${dryRun ? 'true' : 'false'}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify({ dry_run: dryRun, items }),
  })) as ImportResponse;
}

// ─── Advanced reports ─────────────────────────────────────────────

export async function reportsRetention(
  accessToken: string,
  weeks = 8,
): Promise<RetentionResponse> {
  return (await apiFetch(`/admin/reports/retention?weeks=${weeks}`, {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as RetentionResponse;
}

export async function reportsFunnel(
  accessToken: string,
  days = 30,
): Promise<FunnelResponse> {
  return (await apiFetch(`/admin/reports/funnel?days=${days}`, {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as FunnelResponse;
}

export async function reportsBookEngagement(
  accessToken: string,
  bookUUID: string,
): Promise<BookEngagement> {
  return (await apiFetch(
    `/admin/reports/book-engagement?book_uuid=${encodeURIComponent(bookUUID)}`,
    { method: 'GET', headers: { ...authHeaders(accessToken) } },
  )) as BookEngagement;
}

export async function reportsAudit(
  accessToken: string,
  params: {
    limit?: number;
    offset?: number;
    action?: string;
    admin_email?: string;
    ip?: string;
    target_kind?: string;
    q?: string;
    from?: string;
    to?: string;
  } = {},
): Promise<{ items: AuditItem[]; limit: number; offset: number }> {
  const qs = new URLSearchParams({
    limit: String(params.limit ?? 50),
    offset: String(params.offset ?? 0),
  });
  if (params.action) qs.set('action', params.action);
  if (params.admin_email) qs.set('admin_email', params.admin_email);
  if (params.ip) qs.set('ip', params.ip);
  if (params.target_kind) qs.set('target_kind', params.target_kind);
  if (params.q) qs.set('q', params.q);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  return (await apiFetch(`/admin/reports/audit?${qs.toString()}`, {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as { items: AuditItem[]; limit: number; offset: number };
}

export async function listAdmins(accessToken: string, q = '', limit = 50, offset = 0): Promise<ListAdminsResponse> {
  const qs = new URLSearchParams({ q, limit: String(limit), offset: String(offset) }).toString();
  return (await apiFetch(`/admin/admins?${qs}`, {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as ListAdminsResponse;
}

export async function createAdmin(accessToken: string, payload: { email: string; password: string; role?: string }): Promise<AdminListItem> {
  return (await apiFetch('/admin/admins', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify(payload),
  })) as AdminListItem;
}

export async function updateAdmin(
  accessToken: string,
  uuid: string,
  payload: { role?: string; password?: string },
): Promise<AdminListItem> {
  return (await apiFetch(`/admin/admins/${encodeURIComponent(uuid)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify(payload),
  })) as AdminListItem;
}

export async function deleteAdmin(accessToken: string, uuid: string): Promise<void> {
  await apiFetch(`/admin/admins/${encodeURIComponent(uuid)}`, {
    method: 'DELETE',
    headers: { ...authHeaders(accessToken) },
  });
}

export async function listCategories(accessToken: string, q = '', limit = 200, offset = 0): Promise<ListCategoriesResponse> {
  const qs = new URLSearchParams({ q, limit: String(limit), offset: String(offset) }).toString();
  return (await apiFetch(`/admin/categories?${qs}`, {
    method: 'GET',
    headers: { ...authHeaders(accessToken) },
  })) as ListCategoriesResponse;
}

export async function changeMyPassword(
  accessToken: string,
  payload: { old_password: string; new_password: string },
): Promise<{ status: 'ok' }> {
  return (await apiFetch('/admin/auth/change-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(accessToken) },
    body: JSON.stringify(payload),
  })) as { status: 'ok' };
}

